import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ServiceItemDetail,
  BillingFrequency,
  ServiceDuration,
  ChargeType,
  PricingModel
} from "@/pages/settings/pages/products-v2/types";
import {
  BILLING_FREQUENCY_OPTIONS,
  SERVICE_DURATION_OPTIONS
} from "@/pages/settings/pages/products-v2/consts";
import {
  getBillingFrequencyLabel,
  getBillingPeriodLabel,
  getServiceDurationLabel,
  getServiceDurationInMonths,
  getEqPeriodInMonths,
  computeRecurringPaymentCount,
  parseQuantityInput,
  formatQuantityInput,
  isValidQuantity,
  recordedTotalBeforeDiscount,
  monthsBetween,
  recordedPeriodInMonths,
  discountOf
} from "../consts";
import { serviceEndDateAfterMonths } from "../utils";
import {
  isBillingStartDateLockedByFrequency,
  isStartDateLocked
} from "../startDateLock";
import {
  ContractServiceData,
  DiscountType,
  PaymentType,
  ScheduledPaymentDraft,
  SplitPaymentData
} from "../types";
import { toDateOnlyISO } from "@/utils/datetime";
import {
  ContractRevenueItem,
  EqPaymentsBillingFrequency
} from "@/pages/contracts/types";
import {
  useCreateRevenueItem,
  useUpdateRevenueItem,
  CreateRevenueItemRequest
} from "@/pages/contracts/hooks";

interface UseAddServiceFormProps {
  selectedService: ServiceItemDetail | null;
  contractId: string | undefined;
  onAddToContract?: (serviceData: ContractServiceData) => void;
  onClose: () => void;
  editingRevenueItem?: ContractRevenueItem | null;
  // When provided the form never touches the API — the built request is
  // handed to the caller (contract duplication edits a local draft).
  onLocalSubmit?: (
    request: CreateRevenueItemRequest,
    editingRevenueItemId?: string
  ) => void;
  // Update terms: a new line starts on the amendment's effective date.
  defaultServiceStartDate?: string;
  // Update terms allows moving a fixed term's end date to shorten or prolong the period.
  allowServiceEndDateEdit?: boolean;
  // No copy of this contract is on screen yet to show the row in, so the
  // drawer stays open until the save lands.
  awaitSave?: boolean;
  // Update terms asks for the start dates to be locked; a lock applies only where
  // the date has a value to hold.
  lockServiceStartDate?: boolean;
  lockBillingStartDate?: boolean;
  isBillingFrequencyLocked?: boolean;
  // Service notes: the request carries the note only while the drawer shows it.
  includeNote?: boolean;
}

export const useAddServiceForm = ({
  selectedService,
  contractId,
  onAddToContract,
  onClose,
  editingRevenueItem,
  onLocalSubmit,
  defaultServiceStartDate,
  allowServiceEndDateEdit = false,
  awaitSave = false,
  lockServiceStartDate = false,
  lockBillingStartDate = false,
  isBillingFrequencyLocked = false,
  includeNote = false
}: UseAddServiceFormProps) => {
  const { t } = useTranslation();
  const createRevenueItemMutation = useCreateRevenueItem();
  const updateRevenueItemMutation = useUpdateRevenueItem();

  const isEditMode = !!editingRevenueItem;

  const [selectedPricingId, setSelectedPricingId] = useState<string>("");
  const [billingStartDate, setBillingStartDate] = useState<string>("");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [serviceStartDate, setServiceStartDate] = useState<string>("");
  const [isServiceDatePickerOpen, setIsServiceDatePickerOpen] =
    useState<boolean>(false);
  // The raw text is the source of truth: a number-bound input wipes the "-" and
  // the "." before the rest of the number arrives.
  const [quantityInput, setQuantityInput] = useState<string>("1");
  const [discount, setDiscount] = useState<number>(0);
  const [discountType, setDiscountType] = useState<DiscountType>("percentage");
  const [showDiscountInput, setShowDiscountInput] = useState<boolean>(false);
  const [customBillingFrequency, setCustomBillingFrequency] = useState<
    number | null
  >(BillingFrequency.MONTHLY);
  const [customServiceDuration, setCustomServiceDuration] = useState<
    number | null
  >(null);
  const [customServiceEndDate, setCustomServiceEndDate] = useState<string>("");
  // Only a date the user moved overrides the term-derived one.
  const [isServiceEndDateEdited, setIsServiceEndDateEdited] = useState(false);
  const [
    isCustomServiceEndDatePickerOpen,
    setIsCustomServiceEndDatePickerOpen
  ] = useState<boolean>(false);
  const [isEmptyTermClick, setIsEmptyTermClick] = useState<boolean>(false);
  const [showOptOutDate, setShowOptOutDate] = useState<boolean>(false);
  const [optOutDate, setOptOutDate] = useState<string>("");
  const [isOptOutDatePickerOpen, setIsOptOutDatePickerOpen] =
    useState<boolean>(false);
  const [customPrice, setCustomPrice] = useState<number>(0);
  // Only a price the user typed stands in for a line that records none.
  const [isPriceEdited, setIsPriceEdited] = useState(false);
  const [showSplitPayment, setShowSplitPayment] = useState<boolean>(false);
  const [splitPaymentData, setSplitPaymentData] =
    useState<SplitPaymentData | null>(null);
  const [paymentType, setPaymentType] = useState<PaymentType>("equal");
  const [scheduledPayments, setScheduledPayments] = useState<
    ScheduledPaymentDraft[]
  >([]);
  const [autoRenewal, setAutoRenewal] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");

  const selectedPricingModel = useMemo(() => {
    if (!selectedService) return null;
    return selectedService.pricingModel as PricingModel;
  }, [selectedService]);

  const isFlexiblePricing = useMemo(() => {
    return selectedPricingModel === PricingModel.FLEXIBLE;
  }, [selectedPricingModel]);

  const isFlatRatePricing = useMemo(() => {
    return selectedPricingModel === PricingModel.FLAT_RATE;
  }, [selectedPricingModel]);

  useEffect(() => {
    if (!editingRevenueItem && defaultServiceStartDate) {
      setServiceStartDate(defaultServiceStartDate);
      setBillingStartDate(defaultServiceStartDate);
    }
  }, [editingRevenueItem, defaultServiceStartDate]);

  // Initialize form with editing revenue item data
  useEffect(() => {
    if (editingRevenueItem) {
      setSelectedPricingId(editingRevenueItem.serviceItemPricingId || "");
      setBillingStartDate(editingRevenueItem.billingStartDate || "");
      setServiceStartDate(editingRevenueItem.serviceStartDate || "");
      // ?? not || — a stored 0 has to surface as the rejected value it is
      // rather than be silently saved back as 1.
      setQuantityInput(formatQuantityInput(editingRevenueItem.quantity ?? 1));
      setDiscount(editingRevenueItem.discountAmount || 0);
      setDiscountType(
        editingRevenueItem.isDiscountPercentage ? "percentage" : "amount"
      );
      setShowDiscountInput((editingRevenueItem.discountAmount || 0) > 0);
      setCustomBillingFrequency(
        editingRevenueItem.customBillingFrequency ?? BillingFrequency.CUSTOM
      );
      setCustomServiceDuration(
        editingRevenueItem.customServiceDuration ?? null
      );
      if (editingRevenueItem.customServiceDuration === ServiceDuration.CUSTOM) {
        setCustomServiceEndDate(editingRevenueItem.serviceEndDate || "");
      }
      setShowOptOutDate(!!editingRevenueItem.customOptOutDate);
      setOptOutDate(editingRevenueItem.customOptOutDate || "");
      setAutoRenewal(editingRevenueItem.autoRenewal || false);
      setNote(editingRevenueItem.note ?? "");

      // Flat rate prices a whole period and so persists no unit price at all —
      // its field is recovered from the line's own total. Any other line shows
      // the price it recorded, or nothing: standing a whole-line total in for a
      // missing per-unit price is what the total then multiplied back out.
      setCustomPrice(
        editingRevenueItem.pricePerUnit ??
          (isFlatRatePricing
            ? (recordedTotalBeforeDiscount(editingRevenueItem) ?? 0)
            : 0)
      );
      setIsPriceEdited(false);

      const savedScheduledPayments = (
        editingRevenueItem.scheduledPayments ?? []
      )
        .filter((payment) => !payment.isDeleted)
        .sort((first, second) => first.paymentNumber - second.paymentNumber);

      if (savedScheduledPayments.length > 0) {
        setPaymentType("scheduled");
        // A scheduled split leaves the equal-payment columns empty, so the
        // block below never reopens the schedule for it.
        setShowSplitPayment(true);
        setSplitPaymentData({
          numberOfPayments: savedScheduledPayments.length,
          billingFrequencyLabel: t(
            "contractTerms.billingFrequencyLabels.scheduled"
          ),
          billingFrequency: EqPaymentsBillingFrequency.MONTHS
        });
        setScheduledPayments(
          savedScheduledPayments.map((payment) => ({
            id: payment.id,
            // The API returns a timestamp; the row renders and re-submits a
            // date only, and splitting the string keeps the calendar day the
            // user picked instead of shifting it through the local timezone.
            billingDate: payment.billingDate.split("T")[0],
            amount: payment.amount ?? 0,
            percentage: payment.percentage ?? 0
          }))
        );
      }

      if (editingRevenueItem.eqPaymentsNumOfPayments) {
        setShowSplitPayment(true);
        setSplitPaymentData({
          numberOfPayments: editingRevenueItem.eqPaymentsNumOfPayments,
          billingFrequencyLabel:
            editingRevenueItem.eqPaymentsBillingFrequency?.toString() || "",
          billingFrequency:
            editingRevenueItem.eqPaymentsBillingFrequency ??
            EqPaymentsBillingFrequency.MONTHS
        });
      }
    }
    // `t` is intentionally omitted: this effect seeds the form once per edited
    // line, and re-running it on a language change would discard user edits.
    // The pricing model is listed because the seeded price depends on it; it
    // only ever changes alongside the line being edited, so naming it here
    // costs no re-seed and keeps the omission above to the one it explains.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRevenueItem, isFlatRatePricing]);

  useEffect(() => {
    if (
      selectedService &&
      selectedService.pricing.length === 1 &&
      !editingRevenueItem
    ) {
      setSelectedPricingId(selectedService.pricing[0].id);
    }
  }, [selectedService, editingRevenueItem]);

  const selectedPricing = useMemo(() => {
    if (!selectedService || !selectedPricingId) return null;
    return (
      selectedService.pricing.find((p) => p.id === selectedPricingId) || null
    );
  }, [selectedService, selectedPricingId]);

  // An existing line records the terms it was saved under, and the catalog
  // pricing row behind it can be edited afterwards. Until the user picks a
  // different term, the line's own values are what the drawer shows and
  // re-submits; picking another term re-derives everything from that term.
  const recordedTerms = useMemo(() => {
    if (!editingRevenueItem) return null;
    const keepsRecordedPricing =
      selectedPricingId === (editingRevenueItem.serviceItemPricingId ?? "");
    return keepsRecordedPricing ? editingRevenueItem : null;
  }, [editingRevenueItem, selectedPricingId]);

  const isPricingPerPeriod = isFlexiblePricing || isFlatRatePricing;

  const parsedQuantity = useMemo(
    () => parseQuantityInput(quantityInput),
    [quantityInput]
  );

  const isQuantityInvalid =
    !isFlatRatePricing && !isValidQuantity(parsedQuantity);

  // Mid-typing the quantity is NaN; the arithmetic falls back to 0 so no total
  // renders as NaN while the line is incomplete anyway.
  const quantity = Number.isFinite(parsedQuantity) ? parsedQuantity : 0;

  const isCustomServiceDuration = useMemo(() => {
    return (
      selectedPricing?.serviceItemPeriods === ServiceDuration.CUSTOM ||
      selectedPricingModel === PricingModel.FLEXIBLE
    );
  }, [selectedPricing?.serviceItemPeriods, selectedPricingModel]);

  // null is a recorded state, not an absent one: a legacy per-unit line can
  // hold no unit price at all. Reading it through `??` would hand that line the
  // catalog's price — the very substitution this resolution exists to stop — so
  // an existing line's own value wins outright, absence included.
  const effectivePrice = useMemo<number | null>(() => {
    // An existing line that records no unit price has none to show on either
    // branch until the user enters one — the basis below carries its
    // arithmetic meanwhile. Seeding this field from the line's total, as the
    // open-duration branch once did, put a whole-line figure in a per-unit
    // slot and multiplied it back out again.
    if (
      recordedTerms?.pricePerUnit == null &&
      recordedTerms &&
      !isPriceEdited
    ) {
      return null;
    }
    if (isCustomServiceDuration) return customPrice;
    if (recordedTerms) return recordedTerms.pricePerUnit;
    return selectedPricing?.price ?? 0;
  }, [
    isCustomServiceDuration,
    customPrice,
    isPriceEdited,
    recordedTerms,
    selectedPricing?.price
  ]);

  // The name the line was sold under. Renaming the catalog item must not
  // relabel an existing line, on screen or in what the panel resubmits — so the
  // panel and the payload read the same resolution rather than each picking a
  // source.
  //
  // Read from the line itself rather than through `recordedTerms`, which the
  // other resolutions use: that one goes null the moment a different pricing
  // term is picked, and the name is not a pricing term. Gating it there would
  // let choosing a term rename the line. The drawer opens on the line's own
  // service item and offers no way to switch items, so the recorded name is
  // always the right one for an existing line; a new line has none and takes
  // the catalog's, which is all the request type accepts — its field is
  // non-nullable.
  const effectiveServiceItemName = useMemo(
    () => editingRevenueItem?.serviceItemName ?? selectedService?.name ?? "",
    [editingRevenueItem?.serviceItemName, selectedService?.name]
  );

  const isOneTimeCharge = useMemo(() => {
    return selectedService?.chargeType === ChargeType.ONE_TIME;
  }, [selectedService?.chargeType]);

  const isCustomDurationSelected =
    customServiceDuration === ServiceDuration.CUSTOM;

  const hasExplicitEndDate =
    isCustomDurationSelected ||
    (allowServiceEndDateEdit && isServiceEndDateEdited);

  // The period the line actually runs for: the user's pick where the term
  // leaves the duration open, otherwise the line's own recorded duration, and
  // only then the catalog term's.
  const effectiveServiceDuration = useMemo<ServiceDuration | null>(() => {
    if (isCustomServiceDuration) {
      return (customServiceDuration as ServiceDuration | null) ?? null;
    }
    // An existing line's own value wins, absence included — substituting the
    // catalog's term rewrites a field the line deliberately left unset.
    if (recordedTerms) return recordedTerms.customServiceDuration;
    return selectedPricing?.serviceItemPeriods ?? null;
  }, [
    isCustomServiceDuration,
    customServiceDuration,
    recordedTerms,
    selectedPricing?.serviceItemPeriods
  ]);

  const effectiveDurationInMonths = useMemo(() => {
    if (isOneTimeCharge) return 1;
    if (hasExplicitEndDate && serviceStartDate && customServiceEndDate) {
      return monthsBetween(serviceStartDate, customServiceEndDate);
    }
    if (isCustomServiceDuration && customServiceDuration) {
      return getServiceDurationInMonths(customServiceDuration);
    }
    // A line that carries dates states its own length with them; reading the
    // catalog's term instead stretches the total to a period it never ran.
    if (recordedTerms) {
      return recordedPeriodInMonths(
        recordedTerms,
        selectedPricing?.serviceItemPeriods,
        isOneTimeCharge
      );
    }
    return getServiceDurationInMonths(selectedPricing?.serviceItemPeriods);
  }, [
    isOneTimeCharge,
    isCustomServiceDuration,
    hasExplicitEndDate,
    customServiceDuration,
    customServiceEndDate,
    serviceStartDate,
    recordedTerms,
    selectedPricing?.serviceItemPeriods
  ]);

  // A legacy line can record a total but no unit price. Recovering the unit
  // price that total implies gives the arithmetic a basis without ever showing
  // one: an untouched line re-totals to exactly what it recorded, and a
  // quantity or discount edit still moves the total instead of freezing it.
  const recordedUnitPriceBasis = useMemo<number | null>(() => {
    if (!recordedTerms || recordedTerms.pricePerUnit != null) return null;
    const beforeDiscount = recordedTotalBeforeDiscount(recordedTerms);
    if (beforeDiscount == null) return null;

    // Flat rate prices the whole period, so there is nothing to divide out.
    // Divide by exactly what the total will multiply back, so the two cancel
    // and an untouched line re-totals to what it recorded. That means
    // mirroring the same three shapes totalBeforeDiscount branches on —
    // dividing by a period a flexible line never multiplies by, or by a
    // recorded duration where the live one applies, is what inflates it.
    if (isFlatRatePricing) return beforeDiscount;
    const recordedQuantity = recordedTerms.quantity ?? 0;
    if (recordedQuantity === 0) return null;
    if (isFlexiblePricing) return beforeDiscount / recordedQuantity;
    const divisor = recordedQuantity * effectiveDurationInMonths;
    return divisor === 0 ? null : beforeDiscount / divisor;
  }, [
    recordedTerms,
    isFlatRatePricing,
    isFlexiblePricing,
    effectiveDurationInMonths
  ]);

  const totalBeforeDiscount = useMemo(() => {
    // A line that records no unit price falls back to the one its own total
    // implies, never to the catalog's.
    const unitPrice = effectivePrice ?? recordedUnitPriceBasis ?? 0;
    if (isFlexiblePricing) return unitPrice * quantity;
    if (isPricingPerPeriod) return unitPrice;
    return unitPrice * quantity * effectiveDurationInMonths;
  }, [
    isFlexiblePricing,
    isPricingPerPeriod,
    effectivePrice,
    recordedUnitPriceBasis,
    quantity,
    effectiveDurationInMonths
  ]);

  // Flexible pricing has no selectedPricing but is still a valid term — mirror the submit guard so total isn't forced to 0.
  const hasPricingBasis = !!selectedPricing || isFlexiblePricing;

  const calculatedDiscountAmount = useMemo(
    () =>
      hasPricingBasis
        ? discountOf(
            totalBeforeDiscount,
            discount,
            discountType === "percentage"
          )
        : 0,
    [hasPricingBasis, discount, discountType, totalBeforeDiscount]
  );

  const total = useMemo(() => {
    if (!hasPricingBasis) return 0;
    const computed = totalBeforeDiscount - calculatedDiscountAmount;
    // Recovering an unpriced line's basis divides and then re-multiplies, which
    // binary floating point does not always return intact — 1000 at 33% comes
    // back as 1000.0000000000002. The draft compares totals with ===, so that
    // residue alone would badge an untouched row Modified. Anything within a
    // rounding artifact of the figure the line already holds is that figure;
    // a real edit moves the total by orders of magnitude more.
    if (effectivePrice === null) {
      const recordedTotal = recordedTerms?.totalLocal ?? recordedTerms?.total;
      if (recordedTotal != null && Math.abs(computed - recordedTotal) < 0.005) {
        return recordedTotal;
      }
    }
    return computed;
  }, [
    hasPricingBasis,
    totalBeforeDiscount,
    calculatedDiscountAmount,
    effectivePrice,
    recordedTerms
  ]);

  const serviceEndDate = useMemo(() => {
    if (hasExplicitEndDate) return customServiceEndDate;
    // An existing line states its own end date. Deriving one from a term it
    // never recorded hands it the catalog's, which is the substitution this
    // whole resolution exists to stop — so the recorded date stands until the
    // user moves the start date or the duration it was derived against.
    const keepsRecordedPeriod =
      recordedTerms?.serviceEndDate &&
      serviceStartDate === recordedTerms.serviceStartDate &&
      customServiceDuration === recordedTerms.customServiceDuration;
    if (keepsRecordedPeriod) return recordedTerms.serviceEndDate as string;
    return serviceEndDateAfterMonths(
      serviceStartDate,
      effectiveDurationInMonths
    );
  }, [
    serviceStartDate,
    effectiveDurationInMonths,
    hasExplicitEndDate,
    customServiceEndDate,
    customServiceDuration,
    recordedTerms
  ]);

  const isCustomBillingFrequency = useMemo(() => {
    return selectedPricing?.billingFrequency === BillingFrequency.CUSTOM;
  }, [selectedPricing?.billingFrequency]);

  const isCustomBillingFrequencySelected = useMemo(() => {
    return customBillingFrequency === BillingFrequency.CUSTOM;
  }, [customBillingFrequency]);

  // Once a pricing term already defines a fixed billing frequency, the field
  // is read-only (shown via ServiceTermsSection) and must not be re-editable here.
  const showBillingFrequencyField = useMemo(() => {
    return (
      !selectedPricing || isCustomServiceDuration || isCustomBillingFrequency
    );
  }, [selectedPricing, isCustomServiceDuration, isCustomBillingFrequency]);

  // Same precedence as the period: the user's pick where the field is open,
  // then the line's own recorded cadence, then the catalog term's.
  const effectiveBillingFrequency = useMemo<BillingFrequency | null>(() => {
    if (showBillingFrequencyField) {
      return (customBillingFrequency as BillingFrequency | null) ?? null;
    }
    return (
      recordedTerms?.customBillingFrequency ??
      selectedPricing?.billingFrequency ??
      null
    );
  }, [
    showBillingFrequencyField,
    customBillingFrequency,
    recordedTerms?.customBillingFrequency,
    selectedPricing?.billingFrequency
  ]);

  useEffect(() => {
    if (editingRevenueItem || !selectedPricing) return;
    const pricingFrequency = selectedPricing.billingFrequency;
    if (pricingFrequency == null) return;
    setCustomBillingFrequency(pricingFrequency);
  }, [editingRevenueItem, selectedPricing]);

  useEffect(() => {
    const shouldShowPaymentSchedule =
      (!isOneTimeCharge ||
        isCustomBillingFrequency ||
        isCustomServiceDuration) &&
      isCustomBillingFrequencySelected;
    // A one-time charge shows its schedule behind the split-payment toggle, not
    // behind the billing-frequency fields the flag above reads.
    const isPaymentScheduleVisible = isOneTimeCharge
      ? showSplitPayment
      : shouldShowPaymentSchedule;
    // Seeding a schedule the drawer hides would be cleared below on the next
    // run and re-seeded on the one after, looping forever.
    if (
      shouldShowPaymentSchedule &&
      isPaymentScheduleVisible &&
      !splitPaymentData
    ) {
      const defaultBillingEvery = 2;
      const defaultRecurringPayments = computeRecurringPaymentCount(
        effectiveDurationInMonths,
        defaultBillingEvery,
        EqPaymentsBillingFrequency.MONTHS
      );
      setSplitPaymentData({
        numberOfPayments: isOneTimeCharge
          ? defaultBillingEvery
          : defaultRecurringPayments,
        billingFrequencyLabel: t("contractTerms.everyNPeriod", {
          count: defaultBillingEvery,
          period: t("contractTerms.billingFrequencyLabels.months")
        }),
        billingFrequency: EqPaymentsBillingFrequency.MONTHS,
        billingEvery: defaultBillingEvery
      });
    }
    if (!isPaymentScheduleVisible && splitPaymentData) {
      setSplitPaymentData(null);
      // The schedule is gone from the drawer, so its rows must not be
      // submitted with the line — they'd persist a schedule the user can no
      // longer see or edit.
      setPaymentType("equal");
      setScheduledPayments([]);
    }
  }, [
    isCustomBillingFrequency,
    isCustomServiceDuration,
    isCustomBillingFrequencySelected,
    isOneTimeCharge,
    effectiveDurationInMonths,
    splitPaymentData,
    showSplitPayment,
    t
  ]);

  // Keep recurring payment count in sync when duration changes after the
  // PaymentSchedule has been seeded. Only applies to non-one-time custom
  // recurring billing where billingEvery is tracked.
  useEffect(() => {
    if (!splitPaymentData || isOneTimeCharge) return;
    const billingEvery = splitPaymentData.billingEvery;
    if (billingEvery == null) return;
    const periodInMonths = getEqPeriodInMonths(
      splitPaymentData.billingFrequency
    );
    if (billingEvery * periodInMonths <= 0) return;
    const expected = computeRecurringPaymentCount(
      effectiveDurationInMonths,
      billingEvery,
      splitPaymentData.billingFrequency
    );
    if (expected !== splitPaymentData.numberOfPayments) {
      setSplitPaymentData({
        ...splitPaymentData,
        numberOfPayments: expected
      });
    }
  }, [effectiveDurationInMonths, splitPaymentData, isOneTimeCharge]);

  const pricingOptions = useMemo(() => {
    if (!selectedService) return [];

    return selectedService.pricing.map((pricing) => {
      const isCustomDuration =
        pricing.serviceItemPeriods === ServiceDuration.CUSTOM;

      if (isCustomDuration) {
        return {
          id: pricing.id,
          displayName: t("contractTerms.customPricingAndBilling")
        };
      }

      const billingFrequency = getBillingFrequencyLabel(
        pricing.billingFrequency,
        t
      );
      const durationLabel = getServiceDurationLabel(
        pricing.serviceItemPeriods,
        t
      );
      const period = getBillingPeriodLabel(pricing.billingFrequency, t);

      return {
        id: pricing.id,
        displayName: t("contractTerms.pricingDisplayName", {
          duration: durationLabel,
          frequency: billingFrequency.toLowerCase()
        }),
        subtitle: isFlatRatePricing
          ? t("contractTerms.pricingSubtitleFlatRate", {
              price: pricing.price
            })
          : t("contractTerms.pricingSubtitle", {
              price: pricing.price,
              period
            })
      };
    });
  }, [selectedService, t, isFlatRatePricing]);

  const billingFrequencyOptions = useMemo(() => {
    return BILLING_FREQUENCY_OPTIONS.map((option) => ({
      id: option.id,
      displayName: t(option.tKey)
    }));
  }, [t]);

  const serviceDurationOptions = useMemo(() => {
    return SERVICE_DURATION_OPTIONS.map((option) => ({
      id: option.id,
      displayName:
        option.id === ServiceDuration.CUSTOM ? t("other") : t(option.tKey)
    }));
  }, [t]);

  const handlePaymentTypeChange = useCallback(
    (type: PaymentType) => {
      setPaymentType(type);
      if (type === "scheduled") {
        setScheduledPayments((current) =>
          current.length > 0
            ? current
            : [
                {
                  id: "1",
                  billingDate: billingStartDate || toDateOnlyISO(new Date()),
                  amount: 0,
                  percentage: 0
                }
              ]
        );
      }
    },
    [billingStartDate]
  );

  const handleAddToContract = () => {
    if (
      !selectedService ||
      !hasPricingBasis ||
      !billingStartDate ||
      isQuantityInvalid ||
      (!contractId && !onLocalSubmit)
    )
      return;

    const isScheduledSplit =
      paymentType === "scheduled" && scheduledPayments.length > 0;
    // Blank clears the note.
    const submittedNote = note.trim() ? note : null;

    const revenueItemRequest: CreateRevenueItemRequest = {
      serviceItemId: selectedService.id,
      serviceItemName: effectiveServiceItemName,
      serviceItemPricingId: selectedPricing?.id ?? null,
      serviceStartDate: serviceStartDate || null,
      serviceEndDate: serviceEndDate || null,
      autoRenewal: isOneTimeCharge ? false : autoRenewal,
      quantity: isFlatRatePricing ? null : quantity,
      // Persist the true per-single-unit price for every quantity-based model,
      // not just flexible. effectivePrice is customPrice for custom-duration/
      // flexible terms and the catalog unit price otherwise — the same value
      // PricingSummary shows as "price per unit". Flat-rate has no per-unit
      // notion (quantity is null), so it stays null. A line saved without one
      // keeps that gap too — effectivePrice carries the recorded absence, so no
      // separate guard is needed here.
      pricePerUnit: isFlatRatePricing ? null : effectivePrice,
      isDiscountPercentage: discountType === "percentage",
      discountAmount: discount,
      totalLocal: total,
      billingStartDate,
      billingEndDate: null,
      customBillingFrequency: effectiveBillingFrequency,
      customServiceDuration: effectiveServiceDuration,
      customOptOutDate: showOptOutDate ? optOutDate : null,
      // A scheduled split has no single cadence, so the equal-payment columns
      // stay empty and the schedule itself carries the billing dates. The key
      // is always sent: an empty array is what clears a schedule the line no
      // longer has.
      eqPaymentsNumOfPayments: isScheduledSplit
        ? null
        : (splitPaymentData?.numberOfPayments ?? null),
      eqPaymentsBillingFrequency: isScheduledSplit
        ? null
        : (splitPaymentData?.billingFrequency ?? null),
      scheduledPayments: isScheduledSplit
        ? scheduledPayments.map((payment, index) => ({
            paymentNumber: index + 1,
            billingDate: payment.billingDate,
            amount: payment.amount,
            percentage: payment.percentage
          }))
        : [],
      // An edit sends the note only when it changed, so saving other fields
      // never overwrites a note someone edited after the drawer opened.
      ...(includeNote &&
      (!isEditMode || submittedNote !== (editingRevenueItem?.note ?? null))
        ? { note: submittedNote }
        : {})
    };

    const onServiceAdded = () => {
      const serviceData: ContractServiceData = {
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        selectedPricing,
        quantity,
        discount,
        billingStartDate,
        total
      };
      onAddToContract?.(serviceData);
      handleClose();
    };

    if (onLocalSubmit) {
      onLocalSubmit(revenueItemRequest, editingRevenueItem?.id);
      onServiceAdded();
      return;
    }

    if (!contractId) return;

    const mutateOptions = awaitSave ? { onSuccess: onServiceAdded } : {};
    if (isEditMode && editingRevenueItem) {
      updateRevenueItemMutation.mutate(
        {
          pathParams: { contractId, revenueItemId: editingRevenueItem.id },
          data: revenueItemRequest
        },
        mutateOptions
      );
    } else {
      createRevenueItemMutation.mutate(
        {
          pathParams: { contractId },
          data: revenueItemRequest
        },
        mutateOptions
      );
    }
    if (awaitSave) return;
    // The change is already on screen; the save finishes in the background
    // and is rolled back with a toast if it fails.
    onServiceAdded();
  };

  const handleCustomPriceChange = useCallback((value: number) => {
    setCustomPrice(value);
    setIsPriceEdited(true);
  }, []);

  const handleServiceEndDateChange = (date: string) => {
    setCustomServiceEndDate(date);
    setIsServiceEndDateEdited(true);
  };

  const handleClose = () => {
    setSelectedPricingId("");
    // The hook outlives the drawer, so the effect that seeds these on mount does
    // not re-run for a second add — reset restores the default rather than blanking it.
    setBillingStartDate(defaultServiceStartDate ?? "");
    setIsDatePickerOpen(false);
    setServiceStartDate(defaultServiceStartDate ?? "");
    setIsServiceDatePickerOpen(false);
    setQuantityInput("1");
    setDiscount(0);
    setDiscountType("percentage");
    setShowDiscountInput(false);
    setCustomBillingFrequency(BillingFrequency.MONTHLY);
    setCustomServiceDuration(null);
    setCustomServiceEndDate("");
    setIsServiceEndDateEdited(false);
    setIsCustomServiceEndDatePickerOpen(false);
    setIsEmptyTermClick(false);
    setShowOptOutDate(false);
    setOptOutDate("");
    setIsOptOutDatePickerOpen(false);
    setCustomPrice(0);
    setIsPriceEdited(false);
    setShowSplitPayment(false);
    setSplitPaymentData(null);
    setPaymentType("equal");
    setScheduledPayments([]);
    setAutoRenewal(false);
    setNote("");
    onClose();
  };

  const hasServiceConfig = !!selectedPricingId || isFlexiblePricing;
  // A one-time line has no service period unless its pricing is flexible.
  const isServiceStartDateVisible =
    hasServiceConfig && (!isOneTimeCharge || isFlexiblePricing);

  const isServiceStartDateLocked = isStartDateLocked({
    isLockRequested: lockServiceStartDate,
    isEditingExistingLine: isEditMode,
    existingLineStartDate: editingRevenueItem?.serviceStartDate,
    seededStartDate: defaultServiceStartDate
  });
  const isBillingStartDateLocked =
    isStartDateLocked({
      isLockRequested: lockBillingStartDate,
      isEditingExistingLine: isEditMode,
      existingLineStartDate: editingRevenueItem?.billingStartDate,
      seededStartDate: defaultServiceStartDate
    }) ||
    isBillingStartDateLockedByFrequency({
      isBillingFrequencyLocked,
      isEditingExistingLine: isEditMode,
      existingLineBillingStartDate: editingRevenueItem?.billingStartDate,
      seededStartDate: defaultServiceStartDate,
      isServiceStartDateVisible
    });

  const handleServiceStartDateChange = useCallback(
    (date: string) => {
      setServiceStartDate(date);
      // An existing line's billing date stays put while a lock holds it; a lock
      // only ever holds an existing line's date when the line has one.
      if (date && !(isEditMode && isBillingStartDateLocked)) {
        setBillingStartDate(date);
      }
    },
    [setBillingStartDate, isEditMode, isBillingStartDateLocked]
  );

  return {
    t,
    selectedPricingId,
    setSelectedPricingId,
    billingStartDate,
    setBillingStartDate,
    isDatePickerOpen,
    setIsDatePickerOpen,
    serviceStartDate,
    setServiceStartDate: handleServiceStartDateChange,
    isServiceEndDateEditable:
      isCustomDurationSelected || allowServiceEndDateEdit,
    hasExplicitEndDate,
    isServiceDatePickerOpen,
    setIsServiceDatePickerOpen,
    quantityInput,
    setQuantityInput,
    quantity,
    isQuantityInvalid,
    discount,
    setDiscount,
    discountType,
    setDiscountType,
    showDiscountInput,
    setShowDiscountInput,
    customBillingFrequency,
    setCustomBillingFrequency,
    customServiceDuration,
    setCustomServiceDuration,
    customServiceEndDate,
    setCustomServiceEndDate: handleServiceEndDateChange,
    isCustomServiceEndDatePickerOpen,
    setIsCustomServiceEndDatePickerOpen,
    isCustomDurationSelected,
    isEmptyTermClick,
    setIsEmptyTermClick,
    showOptOutDate,
    setShowOptOutDate,
    optOutDate,
    setOptOutDate,
    isOptOutDatePickerOpen,
    setIsOptOutDatePickerOpen,
    customPrice,
    setCustomPrice: handleCustomPriceChange,
    showSplitPayment,
    setShowSplitPayment,
    splitPaymentData,
    setSplitPaymentData,
    paymentType,
    setPaymentType: handlePaymentTypeChange,
    scheduledPayments,
    setScheduledPayments,
    autoRenewal,
    setAutoRenewal,
    note,
    setNote,
    selectedPricing,
    selectedPricingModel,
    isFlexiblePricing,
    isFlatRatePricing,
    isPricingPerPeriod,
    isCustomServiceDuration,
    effectivePrice,
    // Whether the line can supply a price without the user entering one.
    hasRecordedPriceBasis: recordedUnitPriceBasis != null,
    effectiveServiceDuration,
    effectiveBillingFrequency,
    effectiveServiceItemName,
    isOneTimeCharge,
    effectiveDurationInMonths,
    calculatedDiscountAmount,
    total,
    serviceEndDate,
    isCustomBillingFrequency,
    isCustomBillingFrequencySelected,
    showBillingFrequencyField,
    pricingOptions,
    billingFrequencyOptions,
    serviceDurationOptions,
    handleAddToContract,
    handleClose,
    isSubmitting:
      awaitSave &&
      (createRevenueItemMutation.isPending ||
        updateRevenueItemMutation.isPending),
    isEditMode,
    hasServiceConfig,
    isServiceStartDateVisible,
    isServiceStartDateLocked,
    isBillingStartDateLocked
  };
};
